import type { Topic } from '../../../grades/components/GradeLessonPage';

export const intermediateAlgebra: Topic = {
  id: 'intermediate-algebra',
  title: 'Intermediate Algebra',
  icon: 'ri-function-line',
  color: 'violet',
  skills: [
    {
      id: 'quadratic-formula',
      title: 'Quadratic Formula',
      standards: ['ACT Math: Intermediate Algebra', 'Solving Quadratic Equations'],
      explanation: 'The quadratic formula x = (-b ± √(b² - 4ac)) / (2a) is used to solve any quadratic equation in the form ax² + bx + c = 0. This formula always works, even when factoring is difficult or impossible. The discriminant (b² - 4ac) tells us about the nature of solutions: if positive, there are two real solutions; if zero, one real solution; if negative, two complex solutions. Understanding how to apply this formula correctly and interpret the discriminant is essential for ACT Math success.',
      questions: [
        {
          question: 'Solve using the quadratic formula: 2x² + 5x - 3 = 0',
          solution: 'Step 1: Identify a = 2, b = 5, c = -3\nStep 2: Apply formula: x = (-5 ± √(25 - 4(2)(-3))) / (2(2))\nStep 3: Simplify: x = (-5 ± √(25 + 24)) / 4\nStep 4: x = (-5 ± √49) / 4 = (-5 ± 7) / 4\nStep 5: x = 2/4 = 1/2 or x = -12/4 = -3',
          answer: 'x = 1/2 or x = -3'
        },
        {
          question: 'Find the discriminant and determine the nature of solutions: x² - 6x + 9 = 0',
          solution: 'Step 1: Identify a = 1, b = -6, c = 9\nStep 2: Calculate discriminant: b² - 4ac = (-6)² - 4(1)(9)\nStep 3: = 36 - 36 = 0\nStep 4: Since discriminant = 0, there is exactly one real solution',
          answer: 'Discriminant = 0; one real solution'
        },
        {
          question: 'Solve: 3x² - 2x - 8 = 0',
          solution: 'Step 1: a = 3, b = -2, c = -8\nStep 2: x = (2 ± √(4 - 4(3)(-8))) / (2(3))\nStep 3: x = (2 ± √(4 + 96)) / 6\nStep 4: x = (2 ± √100) / 6 = (2 ± 10) / 6\nStep 5: x = 12/6 = 2 or x = -8/6 = -4/3',
          answer: 'x = 2 or x = -4/3'
        },
        {
          question: 'What is the discriminant of 2x² + 3x + 5 = 0? What does it tell us?',
          solution: 'Step 1: a = 2, b = 3, c = 5\nStep 2: Discriminant = b² - 4ac = 9 - 4(2)(5)\nStep 3: = 9 - 40 = -31\nStep 4: Since discriminant < 0, there are two complex (non-real) solutions',
          answer: 'Discriminant = -31; two complex solutions'
        },
        {
          question: 'Solve: x² + 4x + 1 = 0',
          solution: 'Step 1: a = 1, b = 4, c = 1\nStep 2: x = (-4 ± √(16 - 4)) / 2\nStep 3: x = (-4 ± √12) / 2\nStep 4: x = (-4 ± 2√3) / 2\nStep 5: x = -2 ± √3',
          answer: 'x = -2 + √3 or x = -2 - √3'
        },
        {
          question: 'Solve: 5x² - 3x - 2 = 0',
          solution: 'Step 1: a = 5, b = -3, c = -2\nStep 2: x = (3 ± √(9 - 4(5)(-2))) / 10\nStep 3: x = (3 ± √(9 + 40)) / 10\nStep 4: x = (3 ± √49) / 10 = (3 ± 7) / 10\nStep 5: x = 10/10 = 1 or x = -4/10 = -2/5',
          answer: 'x = 1 or x = -2/5'
        },
        {
          question: 'Find all solutions: 4x² + 12x + 9 = 0',
          solution: 'Step 1: a = 4, b = 12, c = 9\nStep 2: x = (-12 ± √(144 - 144)) / 8\nStep 3: x = (-12 ± 0) / 8\nStep 4: x = -12/8 = -3/2\nStep 5: One solution (repeated root)',
          answer: 'x = -3/2'
        },
        {
          question: 'Solve: x² - 8x + 15 = 0 using the quadratic formula',
          solution: 'Step 1: a = 1, b = -8, c = 15\nStep 2: x = (8 ± √(64 - 60)) / 2\nStep 3: x = (8 ± √4) / 2\nStep 4: x = (8 ± 2) / 2\nStep 5: x = 5 or x = 3',
          answer: 'x = 5 or x = 3'
        },
        {
          question: 'Solve: 6x² + x - 2 = 0',
          solution: 'Step 1: a = 6, b = 1, c = -2\nStep 2: x = (-1 ± √(1 - 4(6)(-2))) / 12\nStep 3: x = (-1 ± √(1 + 48)) / 12\nStep 4: x = (-1 ± 7) / 12\nStep 5: x = 6/12 = 1/2 or x = -8/12 = -2/3',
          answer: 'x = 1/2 or x = -2/3'
        },
        {
          question: 'Solve: 2x² - 7x + 3 = 0',
          solution: 'Step 1: a = 2, b = -7, c = 3\nStep 2: x = (7 ± √(49 - 24)) / 4\nStep 3: x = (7 ± √25) / 4\nStep 4: x = (7 ± 5) / 4\nStep 5: x = 3 or x = 1/2',
          answer: 'x = 3 or x = 1/2'
        }
      ]
    },
    {
      id: 'radical-expressions',
      title: 'Radical Expressions',
      standards: ['ACT Math: Intermediate Algebra', 'Simplifying Radicals'],
      explanation: 'Radical expressions involve roots, most commonly square roots (√) and cube roots (∛). Simplifying radicals requires factoring out perfect squares or perfect cubes from under the radical sign. Key rules include: √(ab) = √a · √b, √(a/b) = √a / √b, and √(a²) = |a|. Rationalizing denominators means eliminating radicals from the bottom of fractions by multiplying by a conjugate or appropriate radical. Operations with radicals follow similar rules to operations with variables, but only like radicals can be combined.',
      questions: [
        {
          question: 'Simplify: √72',
          solution: 'Step 1: Factor 72 = 36 × 2\nStep 2: √72 = √(36 × 2)\nStep 3: = √36 · √2\nStep 4: = 6√2',
          answer: '6√2'
        },
        {
          question: 'Simplify: √50 + √32',
          solution: 'Step 1: √50 = √(25 × 2) = 5√2\nStep 2: √32 = √(16 × 2) = 4√2\nStep 3: 5√2 + 4√2 = 9√2',
          answer: '9√2'
        },
        {
          question: 'Rationalize the denominator: 6/√3',
          solution: 'Step 1: Multiply by √3/√3\nStep 2: (6 · √3) / (√3 · √3)\nStep 3: 6√3 / 3\nStep 4: = 2√3',
          answer: '2√3'
        },
        {
          question: 'Simplify: √(48x⁴y⁶)',
          solution: 'Step 1: Factor: √(16 · 3 · x⁴ · y⁶)\nStep 2: = √16 · √3 · √(x⁴) · √(y⁶)\nStep 3: = 4 · √3 · x² · y³\nStep 4: = 4x²y³√3',
          answer: '4x²y³√3'
        },
        {
          question: 'Multiply and simplify: √6 · √15',
          solution: 'Step 1: √6 · √15 = √(6 × 15)\nStep 2: = √90\nStep 3: = √(9 × 10)\nStep 4: = 3√10',
          answer: '3√10'
        },
        {
          question: 'Rationalize: 10/(3 + √5)',
          solution: 'Step 1: Multiply by conjugate: (3 - √5)/(3 - √5)\nStep 2: = 10(3 - √5) / ((3 + √5)(3 - √5))\nStep 3: = (30 - 10√5) / (9 - 5)\nStep 4: = (30 - 10√5) / 4 = (15 - 5√5) / 2',
          answer: '(15 - 5√5)/2'
        },
        {
          question: 'Simplify: ∛54',
          solution: 'Step 1: Factor 54 = 27 × 2\nStep 2: ∛54 = ∛(27 × 2)\nStep 3: = ∛27 · ∛2\nStep 4: = 3∛2',
          answer: '3∛2'
        },
        {
          question: 'Simplify: 3√12 - 2√27',
          solution: 'Step 1: 3√12 = 3√(4 × 3) = 3 · 2√3 = 6√3\nStep 2: 2√27 = 2√(9 × 3) = 2 · 3√3 = 6√3\nStep 3: 6√3 - 6√3 = 0',
          answer: '0'
        },
        {
          question: 'Simplify: √(18a³b⁵)',
          solution: 'Step 1: = √(9 · 2 · a² · a · b⁴ · b)\nStep 2: = √9 · √(a²) · √(b⁴) · √(2ab)\nStep 3: = 3 · a · b² · √(2ab)\nStep 4: = 3ab²√(2ab)',
          answer: '3ab²√(2ab)'
        },
        {
          question: 'Rationalize: 8/(√7 - √3)',
          solution: 'Step 1: Multiply by (√7 + √3)/(√7 + √3)\nStep 2: = 8(√7 + √3) / ((√7 - √3)(√7 + √3))\nStep 3: = (8√7 + 8√3) / (7 - 3)\nStep 4: = (8√7 + 8√3) / 4 = 2√7 + 2√3',
          answer: '2√7 + 2√3'
        }
      ]
    },
    {
      id: 'rational-expressions',
      title: 'Rational Expressions',
      standards: ['ACT Math: Intermediate Algebra', 'Algebraic Fractions'],
      explanation: 'Rational expressions are fractions where the numerator and denominator are polynomials. Simplifying rational expressions involves factoring both the numerator and denominator, then canceling common factors. When adding or subtracting rational expressions, find a common denominator first. When multiplying, factor everything first, then cancel common factors before multiplying across. When dividing, multiply by the reciprocal. Always state restrictions on the variable (values that make the denominator zero).',
      questions: [
        {
          question: 'Simplify: (x² - 9)/(x² - 6x + 9)',
          solution: 'Step 1: Factor numerator: x² - 9 = (x + 3)(x - 3)\nStep 2: Factor denominator: x² - 6x + 9 = (x - 3)²\nStep 3: = (x + 3)(x - 3) / (x - 3)²\nStep 4: Cancel (x - 3): = (x + 3)/(x - 3)\nStep 5: Restriction: x ≠ 3',
          answer: '(x + 3)/(x - 3), x ≠ 3'
        },
        {
          question: 'Multiply: (x² - 4)/(x + 5) · (x + 5)/(x - 2)',
          solution: 'Step 1: Factor: (x + 2)(x - 2)/(x + 5) · (x + 5)/(x - 2)\nStep 2: Cancel (x + 5) and (x - 2)\nStep 3: = x + 2\nStep 4: Restrictions: x ≠ -5, x ≠ 2',
          answer: 'x + 2, x ≠ -5, 2'
        },
        {
          question: 'Add: 3/x + 5/(x + 2)',
          solution: 'Step 1: Common denominator: x(x + 2)\nStep 2: = 3(x + 2)/(x(x + 2)) + 5x/(x(x + 2))\nStep 3: = (3x + 6 + 5x)/(x(x + 2))\nStep 4: = (8x + 6)/(x(x + 2))\nStep 5: = 2(4x + 3)/(x(x + 2))',
          answer: '2(4x + 3)/(x(x + 2))'
        },
        {
          question: 'Divide: (x² - 1)/(x + 3) ÷ (x + 1)/(x² - 9)',
          solution: 'Step 1: Multiply by reciprocal: (x² - 1)/(x + 3) · (x² - 9)/(x + 1)\nStep 2: Factor: (x + 1)(x - 1)/(x + 3) · (x + 3)(x - 3)/(x + 1)\nStep 3: Cancel (x + 1) and (x + 3)\nStep 4: = (x - 1)(x - 3)',
          answer: '(x - 1)(x - 3)'
        },
        {
          question: 'Simplify: (2x² + 5x - 3)/(2x² - x - 1)',
          solution: 'Step 1: Factor numerator: (2x - 1)(x + 3)\nStep 2: Factor denominator: (2x + 1)(x - 1)\nStep 3: No common factors\nStep 4: = (2x - 1)(x + 3)/((2x + 1)(x - 1))\nStep 5: Restrictions: x ≠ 1, x ≠ -1/2',
          answer: '(2x - 1)(x + 3)/((2x + 1)(x - 1))'
        },
        {
          question: 'Subtract: 4/(x - 3) - 2/(x + 1)',
          solution: 'Step 1: Common denominator: (x - 3)(x + 1)\nStep 2: = 4(x + 1)/((x - 3)(x + 1)) - 2(x - 3)/((x - 3)(x + 1))\nStep 3: = (4x + 4 - 2x + 6)/((x - 3)(x + 1))\nStep 4: = (2x + 10)/((x - 3)(x + 1))\nStep 5: = 2(x + 5)/((x - 3)(x + 1))',
          answer: '2(x + 5)/((x - 3)(x + 1))'
        },
        {
          question: 'Simplify: (x³ - 8)/(x² - 4)',
          solution: 'Step 1: Factor numerator (difference of cubes): (x - 2)(x² + 2x + 4)\nStep 2: Factor denominator: (x + 2)(x - 2)\nStep 3: Cancel (x - 2)\nStep 4: = (x² + 2x + 4)/(x + 2)\nStep 5: Restriction: x ≠ 2, x ≠ -2',
          answer: '(x² + 2x + 4)/(x + 2), x ≠ ±2'
        },
        {
          question: 'Multiply: (x² + 3x)/(x² - 9) · (x - 3)/(x)',
          solution: 'Step 1: Factor: x(x + 3)/((x + 3)(x - 3)) · (x - 3)/x\nStep 2: Cancel x, (x + 3), and (x - 3)\nStep 3: = 1\nStep 4: Restrictions: x ≠ 0, x ≠ ±3',
          answer: '1, x ≠ 0, ±3'
        },
        {
          question: 'Add: 2/(x² - 1) + 3/(x + 1)',
          solution: 'Step 1: Factor: 2/((x + 1)(x - 1)) + 3/(x + 1)\nStep 2: Common denominator: (x + 1)(x - 1)\nStep 3: = 2/((x + 1)(x - 1)) + 3(x - 1)/((x + 1)(x - 1))\nStep 4: = (2 + 3x - 3)/((x + 1)(x - 1))\nStep 5: = (3x - 1)/((x + 1)(x - 1))',
          answer: '(3x - 1)/((x + 1)(x - 1))'
        },
        {
          question: 'Simplify: (6x² - 13x - 5)/(3x² + 7x + 2)',
          solution: 'Step 1: Factor numerator: (3x + 1)(2x - 5)\nStep 2: Factor denominator: (3x + 1)(x + 2)\nStep 3: Cancel (3x + 1)\nStep 4: = (2x - 5)/(x + 2)\nStep 5: Restrictions: x ≠ -2, x ≠ -1/3',
          answer: '(2x - 5)/(x + 2), x ≠ -2, -1/3'
        }
      ]
    },
    {
      id: 'complex-numbers',
      title: 'Complex Numbers',
      standards: ['ACT Math: Intermediate Algebra', 'Imaginary Numbers'],
      explanation: 'Complex numbers are numbers in the form a + bi, where a is the real part, b is the imaginary part, and i = √(-1), meaning i² = -1. Complex numbers extend the real number system to include solutions to equations like x² + 1 = 0. Operations with complex numbers follow algebraic rules: add/subtract by combining like terms, multiply using FOIL and replacing i² with -1, and divide by multiplying by the conjugate. The conjugate of a + bi is a - bi, and multiplying a complex number by its conjugate always gives a real number.',
      questions: [
        {
          question: 'Simplify: (3 + 4i) + (2 - 7i)',
          solution: 'Step 1: Combine real parts: 3 + 2 = 5\nStep 2: Combine imaginary parts: 4i - 7i = -3i\nStep 3: Result: 5 - 3i',
          answer: '5 - 3i'
        },
        {
          question: 'Multiply: (2 + 3i)(4 - i)',
          solution: 'Step 1: Use FOIL: 2(4) + 2(-i) + 3i(4) + 3i(-i)\nStep 2: = 8 - 2i + 12i - 3i²\nStep 3: Replace i² with -1: = 8 - 2i + 12i - 3(-1)\nStep 4: = 8 - 2i + 12i + 3\nStep 5: = 11 + 10i',
          answer: '11 + 10i'
        },
        {
          question: 'Find i³',
          solution: 'Step 1: i³ = i² · i\nStep 2: = (-1) · i\nStep 3: = -i',
          answer: '-i'
        },
        {
          question: 'Divide: (6 + 8i)/(1 - i)',
          solution: 'Step 1: Multiply by conjugate: (6 + 8i)/(1 - i) · (1 + i)/(1 + i)\nStep 2: Numerator: (6 + 8i)(1 + i) = 6 + 6i + 8i + 8i²\nStep 3: = 6 + 14i - 8 = -2 + 14i\nStep 4: Denominator: (1 - i)(1 + i) = 1 - i² = 1 + 1 = 2\nStep 5: = (-2 + 14i)/2 = -1 + 7i',
          answer: '-1 + 7i'
        },
        {
          question: 'Simplify: (5 - 2i) - (3 + 4i)',
          solution: 'Step 1: Distribute negative: 5 - 2i - 3 - 4i\nStep 2: Combine real parts: 5 - 3 = 2\nStep 3: Combine imaginary parts: -2i - 4i = -6i\nStep 4: Result: 2 - 6i',
          answer: '2 - 6i'
        },
        {
          question: 'Find i⁴',
          solution: 'Step 1: i⁴ = i² · i²\nStep 2: = (-1)(-1)\nStep 3: = 1',
          answer: '1'
        },
        {
          question: 'Multiply: (3 - 2i)²',
          solution: 'Step 1: (3 - 2i)(3 - 2i)\nStep 2: = 9 - 6i - 6i + 4i²\nStep 3: = 9 - 12i + 4(-1)\nStep 4: = 9 - 12i - 4\nStep 5: = 5 - 12i',
          answer: '5 - 12i'
        },
        {
          question: 'Find the conjugate of 7 - 5i and multiply them',
          solution: 'Step 1: Conjugate is 7 + 5i\nStep 2: (7 - 5i)(7 + 5i)\nStep 3: = 49 + 35i - 35i - 25i²\nStep 4: = 49 - 25(-1)\nStep 5: = 49 + 25 = 74',
          answer: '74'
        },
        {
          question: 'Simplify: i⁷',
          solution: 'Step 1: i⁷ = i⁴ · i³\nStep 2: = 1 · (-i)\nStep 3: = -i\nAlternatively: i⁷ = i⁴ · i² · i = 1 · (-1) · i = -i',
          answer: '-i'
        },
        {
          question: 'Divide: 10/(3 + i)',
          solution: 'Step 1: Multiply by conjugate: 10/(3 + i) · (3 - i)/(3 - i)\nStep 2: Numerator: 10(3 - i) = 30 - 10i\nStep 3: Denominator: (3 + i)(3 - i) = 9 - i² = 9 + 1 = 10\nStep 4: = (30 - 10i)/10\nStep 5: = 3 - i',
          answer: '3 - i'
        }
      ]
    },
    {
      id: 'functions-notation',
      title: 'Functions & Function Notation',
      standards: ['ACT Math: Intermediate Algebra', 'Function Concepts'],
      explanation: 'A function is a relationship where each input (x-value) corresponds to exactly one output (y-value). Function notation f(x) represents the output value when x is the input. To evaluate f(3), substitute 3 for every x in the function. Composite functions like f(g(x)) mean you first evaluate g(x), then use that result as the input for f. The domain is all possible input values, and the range is all possible output values. Understanding function notation, evaluation, composition, and domain/range is crucial for ACT Math.',
      questions: [
        {
          question: 'If f(x) = 3x - 5, find f(4)',
          solution: 'Step 1: Substitute x = 4 into f(x)\nStep 2: f(4) = 3(4) - 5\nStep 3: = 12 - 5\nStep 4: = 7',
          answer: '7'
        },
        {
          question: 'If g(x) = x² + 2x - 1, find g(-3)',
          solution: 'Step 1: Substitute x = -3\nStep 2: g(-3) = (-3)² + 2(-3) - 1\nStep 3: = 9 - 6 - 1\nStep 4: = 2',
          answer: '2'
        },
        {
          question: 'If f(x) = 2x + 1 and g(x) = x², find f(g(3))',
          solution: 'Step 1: First find g(3): g(3) = 3² = 9\nStep 2: Now find f(9): f(9) = 2(9) + 1\nStep 3: = 18 + 1\nStep 4: = 19',
          answer: '19'
        },
        {
          question: 'If h(x) = (x - 2)/(x + 3), what is the domain?',
          solution: 'Step 1: Domain is all real numbers except where denominator = 0\nStep 2: Set denominator equal to zero: x + 3 = 0\nStep 3: x = -3\nStep 4: Domain: all real numbers except x = -3',
          answer: 'All real numbers except x = -3'
        },
        {
          question: 'If f(x) = x² - 4, find f(a + 2)',
          solution: 'Step 1: Substitute (a + 2) for x\nStep 2: f(a + 2) = (a + 2)² - 4\nStep 3: = a² + 4a + 4 - 4\nStep 4: = a² + 4a',
          answer: 'a² + 4a'
        },
        {
          question: 'If f(x) = 3x - 7, find x when f(x) = 11',
          solution: 'Step 1: Set f(x) = 11: 3x - 7 = 11\nStep 2: Add 7 to both sides: 3x = 18\nStep 3: Divide by 3: x = 6',
          answer: 'x = 6'
        },
        {
          question: 'If f(x) = x + 5 and g(x) = 2x - 3, find (f ∘ g)(x)',
          solution: 'Step 1: (f ∘ g)(x) = f(g(x))\nStep 2: = f(2x - 3)\nStep 3: = (2x - 3) + 5\nStep 4: = 2x + 2',
          answer: '2x + 2'
        },
        {
          question: 'If f(x) = √(x - 4), what is the domain?',
          solution: 'Step 1: For square root, expression inside must be ≥ 0\nStep 2: x - 4 ≥ 0\nStep 3: x ≥ 4\nStep 4: Domain: [4, ∞) or x ≥ 4',
          answer: 'x ≥ 4'
        },
        {
          question: 'If f(x) = |2x - 6|, find f(1)',
          solution: 'Step 1: Substitute x = 1\nStep 2: f(1) = |2(1) - 6|\nStep 3: = |2 - 6|\nStep 4: = |-4| = 4',
          answer: '4'
        },
        {
          question: 'If f(x) = x² and g(x) = x - 3, find g(f(2))',
          solution: 'Step 1: First find f(2): f(2) = 2² = 4\nStep 2: Now find g(4): g(4) = 4 - 3\nStep 3: = 1',
          answer: '1'
        }
      ]
    },
    {
      id: 'matrices',
      title: 'Matrices',
      standards: ['ACT Math: Intermediate Algebra', 'Matrix Operations'],
      explanation: 'A matrix is a rectangular array of numbers arranged in rows and columns. Matrix dimensions are written as rows × columns. Matrices can be added or subtracted only if they have the same dimensions, by adding or subtracting corresponding elements. To multiply matrices, the number of columns in the first matrix must equal the number of rows in the second. Matrix multiplication is not commutative (AB ≠ BA). Scalar multiplication means multiplying every element by a constant. Understanding basic matrix operations is essential for ACT Math.',
      questions: [
        {
          question: 'Add the matrices: [2 3] + [5 1]\n                    [4 -1]   [2 6]',
          solution: 'Step 1: Add corresponding elements\nStep 2: [2+5  3+1]\n        [4+2  -1+6]\nStep 3: [7  4]\n        [6  5]',
          answer: '[7 4; 6 5]'
        },
        {
          question: 'Multiply: 3 × [2 -1]\n              [4  5]',
          solution: 'Step 1: Multiply each element by 3\nStep 2: [3(2)  3(-1)]\n        [3(4)  3(5)]\nStep 3: [6  -3]\n        [12  15]',
          answer: '[6 -3; 12 15]'
        },
        {
          question: 'Subtract: [8 2] - [3 5]\n          [1 9]   [2 4]',
          solution: 'Step 1: Subtract corresponding elements\nStep 2: [8-3  2-5]\n        [1-2  9-4]\nStep 3: [5  -3]\n        [-1  5]',
          answer: '[5 -3; -1 5]'
        },
        {
          question: 'What are the dimensions of a matrix with 3 rows and 4 columns?',
          solution: 'Step 1: Dimensions are written as rows × columns\nStep 2: 3 rows and 4 columns\nStep 3: Dimensions: 3 × 4',
          answer: '3 × 4'
        },
        {
          question: 'Multiply: [2 1] × [3]\n                    [5]',
          solution: 'Step 1: (1×2 matrix) × (2×1 matrix) = (1×1 matrix)\nStep 2: [2(3) + 1(5)]\nStep 3: [6 + 5]\nStep 4: [11]',
          answer: '[11]'
        },
        {
          question: 'Find element a₂₃ in matrix: [1 2 3]\n                              [4 5 6]\n                              [7 8 9]',
          solution: 'Step 1: a₂₃ means row 2, column 3\nStep 2: Look at second row: [4 5 6]\nStep 3: Third element in that row is 6',
          answer: '6'
        },
        {
          question: 'Add: [1 -2 3] + [2 0 -1]',
          solution: 'Step 1: Add corresponding elements\nStep 2: [1+2  -2+0  3+(-1)]\nStep 3: [3  -2  2]',
          answer: '[3 -2 2]'
        },
        {
          question: 'Multiply: [1 2] × [5 6]\n          [3 4]   [7 8]',
          solution: 'Step 1: (2×2) × (2×2) = (2×2)\nStep 2: Row 1: [1(5)+2(7)  1(6)+2(8)] = [19 22]\nStep 3: Row 2: [3(5)+4(7)  3(6)+4(8)] = [43 50]\nStep 4: [19 22]\n        [43 50]',
          answer: '[19 22; 43 50]'
        },
        {
          question: 'Multiply: -2 × [3 -4]\n               [1  2]',
          solution: 'Step 1: Multiply each element by -2\nStep 2: [-2(3)  -2(-4)]\n        [-2(1)  -2(2)]\nStep 3: [-6  8]\n        [-2  -4]',
          answer: '[-6 8; -2 -4]'
        },
        {
          question: 'Can you multiply a 2×3 matrix by a 2×2 matrix?',
          solution: 'Step 1: For matrix multiplication, columns of first must equal rows of second\nStep 2: First matrix: 2×3 (3 columns)\nStep 3: Second matrix: 2×2 (2 rows)\nStep 4: 3 ≠ 2, so multiplication is not possible',
          answer: 'No, not possible'
        }
      ]
    },
    {
      id: 'sequences-series',
      title: 'Sequences & Series',
      standards: ['ACT Math: Intermediate Algebra', 'Arithmetic & Geometric Sequences'],
      explanation: 'A sequence is an ordered list of numbers. An arithmetic sequence has a constant difference (d) between consecutive terms: aₙ = a₁ + (n-1)d. A geometric sequence has a constant ratio (r) between consecutive terms: aₙ = a₁ · r^(n-1). A series is the sum of terms in a sequence. The sum of the first n terms of an arithmetic series is Sₙ = n(a₁ + aₙ)/2. The sum of a finite geometric series is Sₙ = a₁(1 - rⁿ)/(1 - r). Understanding how to identify sequence types, find terms, and calculate sums is important for ACT Math.',
      questions: [
        {
          question: 'Find the 10th term of the arithmetic sequence: 3, 7, 11, 15, ...',
          solution: 'Step 1: Identify a₁ = 3, d = 4\nStep 2: Use formula: aₙ = a₁ + (n-1)d\nStep 3: a₁₀ = 3 + (10-1)(4)\nStep 4: = 3 + 36 = 39',
          answer: '39'
        },
        {
          question: 'Find the 6th term of the geometric sequence: 2, 6, 18, 54, ...',
          solution: 'Step 1: Identify a₁ = 2, r = 3\nStep 2: Use formula: aₙ = a₁ · r^(n-1)\nStep 3: a₆ = 2 · 3^(6-1)\nStep 4: = 2 · 3⁵ = 2 · 243 = 486',
          answer: '486'
        },
        {
          question: 'Find the sum of the first 8 terms: 5, 9, 13, 17, ...',
          solution: 'Step 1: Arithmetic sequence: a₁ = 5, d = 4\nStep 2: Find a₈: a₈ = 5 + (8-1)(4) = 5 + 28 = 33\nStep 3: Use sum formula: Sₙ = n(a₁ + aₙ)/2\nStep 4: S₈ = 8(5 + 33)/2 = 8(38)/2 = 152',
          answer: '152'
        },
        {
          question: 'What is the common ratio in: 80, 40, 20, 10, ...?',
          solution: 'Step 1: Divide consecutive terms\nStep 2: 40/80 = 1/2\nStep 3: 20/40 = 1/2\nStep 4: Common ratio r = 1/2',
          answer: '1/2'
        },
        {
          question: 'Find the 15th term: 100, 95, 90, 85, ...',
          solution: 'Step 1: Arithmetic: a₁ = 100, d = -5\nStep 2: a₁₅ = 100 + (15-1)(-5)\nStep 3: = 100 + 14(-5)\nStep 4: = 100 - 70 = 30',
          answer: '30'
        },
        {
          question: 'Find the sum of the first 5 terms: 3, 6, 12, 24, ...',
          solution: 'Step 1: Geometric: a₁ = 3, r = 2\nStep 2: Use formula: Sₙ = a₁(1 - rⁿ)/(1 - r)\nStep 3: S₅ = 3(1 - 2⁵)/(1 - 2)\nStep 4: = 3(1 - 32)/(-1) = 3(-31)/(-1) = 93',
          answer: '93'
        },
        {
          question: 'What is the common difference in: -2, 3, 8, 13, ...?',
          solution: 'Step 1: Subtract consecutive terms\nStep 2: 3 - (-2) = 5\nStep 3: 8 - 3 = 5\nStep 4: Common difference d = 5',
          answer: '5'
        },
        {
          question: 'Find the 7th term: 1, -3, 9, -27, ...',
          solution: 'Step 1: Geometric: a₁ = 1, r = -3\nStep 2: a₇ = 1 · (-3)^(7-1)\nStep 3: = 1 · (-3)⁶\nStep 4: = 729',
          answer: '729'
        },
        {
          question: 'Find the sum: 2 + 5 + 8 + 11 + 14 + 17',
          solution: 'Step 1: Arithmetic: a₁ = 2, a₆ = 17, n = 6\nStep 2: Sₙ = n(a₁ + aₙ)/2\nStep 3: S₆ = 6(2 + 17)/2\nStep 4: = 6(19)/2 = 57',
          answer: '57'
        },
        {
          question: 'Which term equals 243 in the sequence: 3, 9, 27, 81, ...?',
          solution: 'Step 1: Geometric: a₁ = 3, r = 3\nStep 2: aₙ = 3 · 3^(n-1) = 243\nStep 3: 3^n = 243 = 3⁵\nStep 4: n = 5 (5th term)',
          answer: '5th term'
        }
      ]
    },
    {
      id: 'logarithms',
      title: 'Logarithms',
      standards: ['ACT Math: Intermediate Algebra', 'Logarithmic Functions'],
      explanation: 'A logarithm is the inverse of an exponential function. log_b(x) = y means b^y = x. Common logarithms use base 10 (log x), and natural logarithms use base e (ln x). Key properties include: log(ab) = log a + log b, log(a/b) = log a - log b, log(a^n) = n·log a, and log_b(b) = 1. To solve logarithmic equations, use these properties to simplify, then convert to exponential form. Understanding logarithms and their properties is essential for ACT Math intermediate algebra questions.',
      questions: [
        {
          question: 'Evaluate: log₂(32)',
          solution: 'Step 1: Ask: 2 to what power equals 32?\nStep 2: 2⁵ = 32\nStep 3: Therefore log₂(32) = 5',
          answer: '5'
        },
        {
          question: 'Simplify: log₅(25) + log₅(5)',
          solution: 'Step 1: log₅(25) = 2 (since 5² = 25)\nStep 2: log₅(5) = 1 (since 5¹ = 5)\nStep 3: 2 + 1 = 3',
          answer: '3'
        },
        {
          question: 'Solve for x: log₃(x) = 4',
          solution: 'Step 1: Convert to exponential form\nStep 2: 3⁴ = x\nStep 3: x = 81',
          answer: 'x = 81'
        },
        {
          question: 'Simplify: log(100) - log(10)',
          solution: 'Step 1: Use property: log(a) - log(b) = log(a/b)\nStep 2: = log(100/10)\nStep 3: = log(10)\nStep 4: = 1',
          answer: '1'
        },
        {
          question: 'Evaluate: log₄(64)',
          solution: 'Step 1: Ask: 4 to what power equals 64?\nStep 2: 4³ = 64\nStep 3: Therefore log₄(64) = 3',
          answer: '3'
        },
        {
          question: 'Simplify: 3·log₂(4)',
          solution: 'Step 1: log₂(4) = 2 (since 2² = 4)\nStep 2: 3 · 2 = 6',
          answer: '6'
        },
        {
          question: 'Solve for x: log(x) + log(5) = log(20)',
          solution: 'Step 1: Use property: log(a) + log(b) = log(ab)\nStep 2: log(5x) = log(20)\nStep 3: 5x = 20\nStep 4: x = 4',
          answer: 'x = 4'
        },
        {
          question: 'Evaluate: log₇(1)',
          solution: 'Step 1: Ask: 7 to what power equals 1?\nStep 2: 7⁰ = 1\nStep 3: Therefore log₇(1) = 0',
          answer: '0'
        },
        {
          question: 'Simplify: log₃(27) - log₃(9)',
          solution: 'Step 1: log₃(27) = 3, log₃(9) = 2\nStep 2: 3 - 2 = 1\nAlternatively: log₃(27/9) = log₃(3) = 1',
          answer: '1'
        },
        {
          question: 'Solve for x: 2^x = 16',
          solution: 'Step 1: Convert to logarithmic form\nStep 2: x = log₂(16)\nStep 3: 2⁴ = 16\nStep 4: x = 4',
          answer: 'x = 4'
        }
      ]
    },
    {
      id: 'exponential-functions',
      title: 'Exponential Functions',
      standards: ['ACT Math: Intermediate Algebra', 'Exponential Growth & Decay'],
      explanation: 'Exponential functions have the form f(x) = a·b^x, where a is the initial value and b is the base. When b > 1, the function represents exponential growth; when 0 < b < 1, it represents exponential decay. The natural exponential function uses base e ≈ 2.718. Key properties: b⁰ = 1, b^m · b^n = b^(m+n), (b^m)^n = b^(mn), and b^m / b^n = b^(m-n). Exponential functions model real-world situations like population growth, compound interest, and radioactive decay. Understanding exponential functions and their properties is crucial for ACT Math.',
      questions: [
        {
          question: 'Evaluate: 2⁵',
          solution: 'Step 1: 2⁵ = 2 × 2 × 2 × 2 × 2\nStep 2: = 32',
          answer: '32'
        },
        {
          question: 'Simplify: 3² · 3⁴',
          solution: 'Step 1: Use property: b^m · b^n = b^(m+n)\nStep 2: 3² · 3⁴ = 3^(2+4)\nStep 3: = 3⁶\nStep 4: = 729',
          answer: '729 or 3⁶'
        },
        {
          question: 'Simplify: (2³)²',
          solution: 'Step 1: Use property: (b^m)^n = b^(mn)\nStep 2: (2³)² = 2^(3·2)\nStep 3: = 2⁶\nStep 4: = 64',
          answer: '64 or 2⁶'
        },
        {
          question: 'Evaluate: 5⁰',
          solution: 'Step 1: Any non-zero number to the power of 0 equals 1\nStep 2: 5⁰ = 1',
          answer: '1'
        },
        {
          question: 'Simplify: 4⁵ / 4²',
          solution: 'Step 1: Use property: b^m / b^n = b^(m-n)\nStep 2: 4⁵ / 4² = 4^(5-2)\nStep 3: = 4³\nStep 4: = 64',
          answer: '64 or 4³'
        },
        {
          question: 'If f(x) = 2^x, find f(4)',
          solution: 'Step 1: Substitute x = 4\nStep 2: f(4) = 2⁴\nStep 3: = 16',
          answer: '16'
        },
        {
          question: 'Simplify: (3x²)³',
          solution: 'Step 1: Apply exponent to each factor\nStep 2: = 3³ · (x²)³\nStep 3: = 27 · x⁶\nStep 4: = 27x⁶',
          answer: '27x⁶'
        },
        {
          question: 'Evaluate: 8^(2/3)',
          solution: 'Step 1: Rewrite as (8^(1/3))²\nStep 2: 8^(1/3) = ∛8 = 2\nStep 3: 2² = 4',
          answer: '4'
        },
        {
          question: 'Simplify: (2⁴ · 2³) / 2⁵',
          solution: 'Step 1: Multiply numerator: 2⁴ · 2³ = 2⁷\nStep 2: Divide: 2⁷ / 2⁵ = 2^(7-5)\nStep 3: = 2²\nStep 4: = 4',
          answer: '4 or 2²'
        },
        {
          question: 'If a population doubles every 3 years and starts at 100, what is the function?',
          solution: 'Step 1: Exponential growth: P(t) = a · b^t\nStep 2: Initial value a = 100\nStep 3: Doubles every 3 years: b = 2, but t is in 3-year periods\nStep 4: P(t) = 100 · 2^(t/3)',
          answer: 'P(t) = 100 · 2^(t/3)'
        }
      ]
    },
    {
      id: 'systems-inequalities',
      title: 'Systems of Inequalities',
      standards: ['ACT Math: Intermediate Algebra', 'Graphing Inequalities'],
      explanation: 'A system of inequalities consists of two or more inequalities with the same variables. The solution is the region where all inequalities overlap when graphed. To graph a linear inequality: first graph the boundary line (solid for ≤ or ≥, dashed for < or >), then shade the appropriate region by testing a point. For systems, shade each inequality and find the overlapping region. Key skills include identifying boundary lines, determining which side to shade, and recognizing the solution region. Systems of inequalities are used in optimization problems and real-world constraints.',
      questions: [
        {
          question: 'Graph the inequality: y > 2x + 1',
          solution: 'Step 1: Graph boundary line y = 2x + 1 (dashed line)\nStep 2: Test point (0,0): 0 > 2(0) + 1? → 0 > 1? No\nStep 3: Shade the region above the line (opposite side from origin)\nStep 4: Solution: all points above the dashed line',
          answer: 'Region above y = 2x + 1 (dashed)'
        },
        {
          question: 'Is (2, 5) a solution to y ≤ 3x - 1?',
          solution: 'Step 1: Substitute x = 2, y = 5\nStep 2: 5 ≤ 3(2) - 1?\nStep 3: 5 ≤ 6 - 1?\nStep 4: 5 ≤ 5? Yes',
          answer: 'Yes'
        },
        {
          question: 'Solve the system: y < x + 2 and y ≥ -x + 1',
          solution: 'Step 1: Graph y = x + 2 (dashed), shade below\nStep 2: Graph y = -x + 1 (solid), shade above\nStep 3: Solution is the overlapping shaded region\nStep 4: Region between the two lines',
          answer: 'Overlapping region between lines'
        },
        {
          question: 'Which inequality represents the region below the line y = -2x + 3?',
          solution: 'Step 1: Below the line means y-values are less than line values\nStep 2: Inequality: y < -2x + 3\nStep 3: Use < (not ≤) if line is dashed',
          answer: 'y < -2x + 3'
        },
        {
          question: 'Is (0, 0) in the solution region of x + y ≤ 5?',
          solution: 'Step 1: Substitute x = 0, y = 0\nStep 2: 0 + 0 ≤ 5?\nStep 3: 0 ≤ 5? Yes',
          answer: 'Yes'
        },
        {
          question: 'Graph: x ≥ -2',
          solution: 'Step 1: This is a vertical line at x = -2\nStep 2: Use solid line (includes x = -2)\nStep 3: Shade to the right (where x-values are greater)\nStep 4: Solution: all points with x ≥ -2',
          answer: 'Region right of x = -2 (solid line)'
        },
        {
          question: 'Solve: y > 2 and x < 3',
          solution: 'Step 1: y > 2: horizontal dashed line at y = 2, shade above\nStep 2: x < 3: vertical dashed line at x = 3, shade left\nStep 3: Solution: overlapping region (upper-left quadrant)\nStep 4: All points where y > 2 AND x < 3',
          answer: 'Region where y > 2 and x < 3'
        },
        {
          question: 'Which point satisfies both y ≥ x and y ≤ 4: (1,3), (5,2), (3,5)?',
          solution: 'Step 1: Test (1,3): 3 ≥ 1? Yes. 3 ≤ 4? Yes. ✓\nStep 2: Test (5,2): 2 ≥ 5? No. ✗\nStep 3: Test (3,5): 5 ≥ 3? Yes. 5 ≤ 4? No. ✗\nStep 4: Only (1,3) satisfies both',
          answer: '(1, 3)'
        },
        {
          question: 'Graph: y ≤ -x + 4',
          solution: 'Step 1: Graph boundary line y = -x + 4 (solid line)\nStep 2: Test (0,0): 0 ≤ -0 + 4? → 0 ≤ 4? Yes\nStep 3: Shade the region containing origin (below line)\nStep 4: Solution: all points on or below the line',
          answer: 'Region on/below y = -x + 4 (solid)'
        },
        {
          question: 'Describe the solution to: x ≥ 0, y ≥ 0, x + y ≤ 6',
          solution: 'Step 1: x ≥ 0: right of y-axis (including axis)\nStep 2: y ≥ 0: above x-axis (including axis)\nStep 3: x + y ≤ 6: on or below line through (6,0) and (0,6)\nStep 4: Solution: triangular region in first quadrant',
          answer: 'Triangle with vertices (0,0), (6,0), (0,6)'
        }
      ]
    }
  ]
};